import { cardinalForExposure } from '@alpinehomeair/common';
import { CSSProperties } from 'react';
import { STRUCTURE_GRID_SIZE } from 'data/constants';
import { ExposureDirectionType, FootprintPolygonsType } from 'data/types';
import { exposureForEdgeRun } from 'utils/edge-exposure';
import { hasAnyFootprintPolygon } from 'utils/footprint-polygons';
import {
  OpeningWallLikeType,
  SpaceEdgeRunGeometryType,
  SpaceFeetGeometryType
} from 'utils/opening-layout-types';
import {
  EMPTY_OPENING_RUN_BOUNDS,
  openingRunBounds,
  openingSpanOnRun,
  orientCardinalWallEdge,
  runFrame
} from 'utils/opening-position';
import {
  EdgeRunExtraType,
  EdgeRunType,
  OpeningEdgeType,
  OpeningPointType,
  OpeningRunBoundsType,
  OpeningSpanType
} from 'utils/opening-position-types';
import { OPENING_BAR_THICKNESS_PX } from 'utils/opening-visuals';
import {
  boundingBoxOfVertices,
  getExteriorPolygonEdges,
  getSpaceVertices,
  SpacePolygonEdge
} from 'utils/space-polygon';
import { outwardNormalForCardinal } from 'utils/wall-segment';

export type OpeningBarRectType = {
  left: number;
  top: number;
  width: number;
  height: number;
  directionDeg: number;
};

export function plainEdgeRun(
  edge: OpeningEdgeType,
  start: OpeningPointType,
  end: OpeningPointType,
  extra: EdgeRunExtraType = {}
): EdgeRunType {
  const lengthFt = Math.hypot(end.x - start.x, end.y - start.y);
  return {
    edge,
    start: { x: start.x, y: start.y },
    end: { x: end.x, y: end.y },
    ...openingRunBounds(lengthFt),
    ...extra
  };
}

export function edgeRunFromPolygonEdge(
  edge: SpacePolygonEdge,
  originFeet: OpeningPointType
): EdgeRunType | null {
  if (!edge.cardinal) {
    return null;
  }
  const oriented = orientCardinalWallEdge(
    { x: edge.p1.x - originFeet.x, y: edge.p1.y - originFeet.y },
    { x: edge.p2.x - originFeet.x, y: edge.p2.y - originFeet.y },
    edge.cardinal
  );
  if (oriented.lengthFt <= 0) {
    return null;
  }
  return plainEdgeRun(edge.cardinal, oriented.start, oriented.end, {
    edgeIndex: edge.edgeIndex,
    outward: edge.outward
  });
}

export function cardinalEdgeRun(
  edge: OpeningEdgeType,
  start: OpeningPointType,
  lengthFt: number,
  extra: EdgeRunExtraType = {}
): EdgeRunType {
  const end =
    edge === 'top' || edge === 'bottom'
      ? { x: start.x + lengthFt, y: start.y }
      : { x: start.x, y: start.y + lengthFt };
  return plainEdgeRun(edge, start, end, {
    outward: outwardNormalForCardinal(edge),
    ...extra
  });
}

export function buildRectEdgeRuns(
  widthFt: number,
  heightFt: number
): EdgeRunType[] {
  return [
    cardinalEdgeRun('top', { x: 0, y: 0 }, widthFt, { edgeIndex: 0 }),
    cardinalEdgeRun('right', { x: widthFt, y: 0 }, heightFt, { edgeIndex: 1 }),
    cardinalEdgeRun('bottom', { x: 0, y: heightFt }, widthFt, {
      edgeIndex: 2
    }),
    cardinalEdgeRun('left', { x: 0, y: 0 }, heightFt, { edgeIndex: 3 })
  ];
}

export function getSpaceEdgeRunsFromFeet(
  space: SpaceEdgeRunGeometryType,
  footprints: FootprintPolygonsType | null
): EdgeRunType[] {
  const widthFt = space.position.width;
  const heightFt = space.position.height;

  const verticesFeet = space.vertices;
  if (!verticesFeet || verticesFeet.length < 3) {
    return buildRectEdgeRuns(widthFt, heightFt);
  }

  const spaceOriginFeet = {
    x: space.position.x,
    y: space.position.y
  };
  const absoluteVertices = verticesFeet.map((vertex) => ({
    x: vertex.x + spaceOriginFeet.x,
    y: vertex.y + spaceOriginFeet.y
  }));
  const edges = getExteriorPolygonEdges(absoluteVertices, footprints);
  const hasFootprint = hasAnyFootprintPolygon(footprints);

  const runs: EdgeRunType[] = [];
  edges.forEach((edge) => {
    const includeEdge = !hasFootprint || edge.isExterior;
    const run = includeEdge
      ? edgeRunFromPolygonEdge(edge, spaceOriginFeet)
      : null;
    if (run) {
      runs.push(run);
    }
  });

  if (runs.length > 0) {
    return runs;
  }

  return buildRectEdgeRuns(widthFt, heightFt);
}

export function getSpaceEdgeRuns(
  space: SpaceEdgeRunGeometryType,
  footprints: FootprintPolygonsType | null
): EdgeRunType[] {
  return getSpaceEdgeRunsFromFeet(
    {
      position: {
        x: space.position.x / STRUCTURE_GRID_SIZE,
        y: space.position.y / STRUCTURE_GRID_SIZE,
        width: space.position.width / STRUCTURE_GRID_SIZE,
        height: space.position.height / STRUCTURE_GRID_SIZE
      },
      vertices: space.vertices?.map((vertex) => ({
        x: vertex.x / STRUCTURE_GRID_SIZE,
        y: vertex.y / STRUCTURE_GRID_SIZE
      }))
    },
    footprints
  );
}

export function spaceToFeetEdgeRunGeometry(
  space: SpaceFeetGeometryType
): SpaceEdgeRunGeometryType {
  const relativeVertices = getSpaceVertices(space);
  const bb = boundingBoxOfVertices(relativeVertices);

  return {
    position: {
      x: (space.position?.x ?? 0) + bb.minX,
      y: (space.position?.y ?? 0) + bb.minY,
      width: bb.width,
      height: bb.height
    },
    vertices: relativeVertices.map((vertex) => ({
      x: vertex.x - bb.minX,
      y: vertex.y - bb.minY
    }))
  };
}

export function runForEdge(
  runs: EdgeRunType[],
  edge: OpeningEdgeType
): EdgeRunType | undefined {
  return runs.find((run) => run.edge === edge);
}

export function runBoundsForEdge(
  runs: EdgeRunType[],
  edge: OpeningEdgeType
): OpeningRunBoundsType {
  return runForEdge(runs, edge) ?? EMPTY_OPENING_RUN_BOUNDS;
}

export function runForWall(
  runs: EdgeRunType[],
  wall: OpeningWallLikeType,
  homeOrientation: ExposureDirectionType | undefined
): EdgeRunType | undefined {
  const byExposure = runs.find(
    (run) => exposureForEdgeRun(run, homeOrientation) === wall.exposure
  );
  if (byExposure) return byExposure;
  const edge = wall.edge ?? cardinalForExposure(wall.exposure, homeOrientation);
  return edge ? runForEdge(runs, edge) : undefined;
}

export function runBoundsForWall(
  runs: EdgeRunType[],
  wall: OpeningWallLikeType,
  homeOrientation: ExposureDirectionType | undefined
): OpeningRunBoundsType {
  return runForWall(runs, wall, homeOrientation) ?? EMPTY_OPENING_RUN_BOUNDS;
}

export function openingBarRect(
  span: OpeningSpanType,
  feetToPx: number = STRUCTURE_GRID_SIZE,
  thicknessPx: number = OPENING_BAR_THICKNESS_PX
): OpeningBarRectType {
  const dx = (span.p2.x - span.p1.x) * feetToPx;
  const dy = (span.p2.y - span.p1.y) * feetToPx;
  return {
    left: span.p1.x * feetToPx,
    top: span.p1.y * feetToPx - thicknessPx / 2,
    width: Math.hypot(dx, dy),
    height: thicknessPx,
    directionDeg: (Math.atan2(dy, dx) * 180) / Math.PI
  };
}

export function openingBarRectOnRun(
  run: EdgeRunType,
  offsetFt: number,
  widthFt: number
): OpeningBarRectType {
  const rect = openingBarRect(openingSpanOnRun(run, offsetFt, widthFt));
  if (rect.width > 0) {
    return rect;
  }
  return { ...rect, directionDeg: runFrame(run).directionDeg };
}

const OPENING_BAR_TRANSFORM_ORIGIN = '0 50%';

export function openingBarStyle(
  rect: OpeningBarRectType,
  translateX: number = 0,
  translateY: number = 0
): CSSProperties {
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
    transformOrigin: OPENING_BAR_TRANSFORM_ORIGIN,
    transform: `translate(${translateX}px, ${translateY}px) rotate(${rect.directionDeg}deg)`
  };
}
